import { useContext, useEffect, useRef } from 'react';
import { ViewContext, ProductsContext, CatalogDispatchContext } from '../store/CatalogContext';
import ReactPaginate from 'react-paginate';

interface PaginationProps {
  group: 'purchased' | 'related' | 'fullCatalog';
  itemsLength: number;
}

export default function Pagination({ group, itemsLength }: PaginationProps) {
  const viewState = useContext(ViewContext);
  const productsState = useContext(ProductsContext);
  const catalogDispatch = useContext(CatalogDispatchContext);

  const viewForGroup = viewState[group];
  const { productPerPage, pageNumber } = viewForGroup;
  const pageCount = Math.ceil(itemsLength / Math.max(productPerPage, 1));
  const startIndex = (pageNumber - 1) * productPerPage;
  const endIndexExclusive = Math.min(startIndex + productPerPage, itemsLength);

  // Helper to dispatch view updates for this group
  const updateView = (partial: Partial<typeof viewForGroup>) => {
    catalogDispatch({
      type: 'VIEW',
      payload: {
        [group]: {
          ...viewForGroup,
          ...partial,
        },
      },
    });

    // store view settings in sessionStorage
    sessionStorage.setItem('catalogView', JSON.stringify({ ...viewState, [group]: { ...viewForGroup, ...partial } }));
  };

  const handleProductsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newProductPerPage = Number(event.target.value);

    updateView({
      productPerPage: newProductPerPage,
      pageNumber: 1,
    });
  };

  const handlePageChange = (event: { selected: number }) => {
    const newPageNumber = event.selected + 1; // zero-based -> 1-based

    if (newPageNumber !== pageNumber) {
      updateView({
        pageNumber: newPageNumber,
      });
    }
  };

  // Clamp pageNumber if data shrinks (e.g., filtering) and current page now out of range
  useEffect(() => {
    if (pageCount > 0 && pageNumber > pageCount) {
      updateView({
        pageNumber: pageCount,
      });
    }
  }, [pageCount, pageNumber, updateView]);

  // used to track initial page number so that scroll behavior can be prevented
  const initialPageNumber = useRef(pageNumber);

  // Smooth scroll to top sentinel (or window) when page changes
  useEffect(() => {
    // First render or context hasn't updated yet, skip
    if (initialPageNumber.current === pageNumber) {
      return;
    }
    initialPageNumber.current = pageNumber;

    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(() => {
      const sentinel = document.getElementById(`${group}-top`);
      if (sentinel) {
        sentinel.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
      } else {
        const scroller = document.scrollingElement || document.documentElement;
        scroller.scrollTo({ top: 0, left: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
      }
    });
  }, [pageNumber, group]);

  // return early if no products are found
  if (!productsState[group]) {
    return null;
  }

  return (
    <div className="pagination-wrap col-12 d-flex flex-column flex-md-row flex-wrap justify-content-between justify-content-md-end align-items-center mt-3 my-2 mt-md-2">
      {pageCount > 1 && (
        <nav className="mb-3 mb-md-0" aria-label="product pagination">
          <ReactPaginate
            className="product-pagination pagination mb-0"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            activeClassName="active"
            activeLinkClassName="active"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="prev-btn page-link"
            nextClassName="page-item"
            nextLinkClassName="next-btn page-link"
            breakLabel="..."
            previousLabel=""
            nextLabel=""
            forcePage={pageNumber - 1}
            pageRangeDisplayed={3}
            onPageChange={handlePageChange}
            pageCount={pageCount}
            renderOnZeroPageCount={null}
          />
        </nav>
      )}
      <div className="ms-4 d-flex flex-grow-0 align-items-center">
        <select
          className="form-select product-per-page d-inline-block me-2"
          name="products-per-page"
          aria-label="Products per page"
          onChange={handleProductsPerPageChange}
          value={productPerPage}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={30}>30</option>
          <option value={40}>40</option>
          <option value={50}>50</option>
          <option value={60}>60</option>
          <option value={70}>70</option>
          <option value={80}>80</option>
          <option value={90}>90</option>
          <option value={100}>100</option>
        </select>
        <span className="d-inline-block text-muted">Per Page</span>
      </div>
      <div className="d-none d-lg-block ms-4">
        <span className="text-muted">
          Showing{' '}
          <strong>
            {itemsLength === 0 ? 0 : startIndex + 1}-{endIndexExclusive}
          </strong>{' '}
          of <strong>{itemsLength}</strong>
        </span>
      </div>
    </div>
  );
}
